/**
 * useTruncateCheck hook
 *
 * Custom hook to detect whether a text element is visually truncated.
 * It compares the element's scrollWidth with its clientWidth to determine
 * if the content overflows its container.
 *
 * This is useful for conditionally showing tooltips or full text only
 * when the text is actually cut off in the UI.
 *
 * Usage:
 * const [ref, isTruncated] = useTruncateCheck([value]);
 * Pass dependencies to control when the truncation calculation should re-run.
 *
 * Attach `ref` to the element you want to measure and use `isTruncated`
 * to control tooltip or overflow behavior.
 *
 * Note:
 * - Re-checks automatically when the element is resized.
 * - Works for horizontal truncation (ellipsis).
 * - Ensure styles include `overflow: hidden` and `text-overflow: ellipsis`,
 *   or use the Bootstrap class `text-truncate`.
 */

import { useEffect, useRef, useState } from 'react';

const useTruncateCheck = deps => {
	const ref = useRef(null);
	const [isTruncated, setIsTruncated] = useState(false);

	useEffect(() => {
		const check = () => {
			const el = ref.current;
			if (!el) return;

			const truncated = el.scrollWidth > el.clientWidth;
			setIsTruncated(prev => (prev !== truncated ? truncated : prev));
		};

		check();

		// Layout changes (e.g. grid view switch) resize the element without changing deps.
		const el = ref.current;
		if (!el) return;
		const observer = new ResizeObserver(check);
		observer.observe(el);
		return () => observer.disconnect();
	}, deps);

	return [ref, isTruncated];
};

export default useTruncateCheck;
